import {
  createQuery,
  createSuspenseQuery
} from "../../utils/queryFactories.js";
import { api } from "../../utils/fetch.js";
import { z } from "zod";

export const GetResourceAgendaResourceResponseSchema = z.object({
  id: z.number(),
  archived: z.boolean(),
  board: z.number(),
  type_description: z.string().nullish(),
  type: z.number().nullable(),
  image_url: z.string().nullish(),
  display_name: z.string().nullish(),
  job_title: z.string().nullish(),
  location: z.string().nullish(),
  description: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  category_value: z.string().nullish(),
  category: z.number().nullable(),
  status_value: z.string().nullish(),
  status: z.number().nullable(),
  tag_values: z.array(z.string().nullable()),
  tag: z.array(z.number()),
  team_name: z.string().nullish(),
  team: z.number().nullable(),
  team_color: z.string().nullish(),
  icon_value: z.string().nullish(),
  icon: z.number().nullable(),
  label: z.string().nullish(),
  hide_agenda: z.boolean(),
  checklists: z.array(z.number()),
  board_name: z.string().nullish(),
  notes_count: z.number(),
  files_count: z.number(),
  checklist_count: z.number(),
  assigned_boardusers: z.array(z.number()),
  can_create_file: z.boolean().optional()
});

type GetResourceAgendaResourceResponse = z.infer<
  typeof GetResourceAgendaResourceResponseSchema
>;

const GetResourceAgendaResourceRequestSchema = z.object({
  resourceId: z.number()
});

type GetResourceAgendaResourceRequest = z.infer<
  typeof GetResourceAgendaResourceRequestSchema
>;

export const useGetResourceAgendaResource = createQuery<
  GetResourceAgendaResourceResponse,
  GetResourceAgendaResourceRequest,
  Error
>({
  queryKey: ["resourceAgendaResource"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    return api<
      GetResourceAgendaResourceRequest,
      GetResourceAgendaResourceResponse
    >({
      method: "GET",
      url: `/resource/:resourceId`,
      requestSchema: GetResourceAgendaResourceRequestSchema,
      responseSchema: GetResourceAgendaResourceResponseSchema,
      body: variables
    });
  }
});

export const useGetResourceAgendaResourceWithSuspense = createSuspenseQuery<
  GetResourceAgendaResourceResponse,
  GetResourceAgendaResourceRequest,
  Error
>({
  queryKey: ["resourceAgendaResourceWithSuspense"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    return api<
      GetResourceAgendaResourceRequest,
      GetResourceAgendaResourceResponse
    >({
      method: "GET",
      url: `/resource/:resourceId`,
      requestSchema: GetResourceAgendaResourceRequestSchema,
      responseSchema: GetResourceAgendaResourceResponseSchema,
      body: variables
    });
  }
});

// Add discriminated union for wf and public requests
const wfRequest = z.object({
  api: z.literal("wf"),
  id: z.string() // Original key for wf endpoint
});
const publicRequest = z.object({
  api: z.literal("public"),
  slug: z.string(),
  id: z.string() // Key for public endpoint
});
const ResourceRequestSchema = z.discriminatedUnion("api", [
  wfRequest,
  publicRequest
]);
const ResourceRequestVariables = z.union([
  wfRequest.omit({ api: true }),
  publicRequest.omit({ api: true })
]);

export type ResourceRequest = z.infer<typeof ResourceRequestSchema>;
export type ResourceResponse = z.infer<
  typeof GetResourceAgendaResourceResponseSchema
>;
const urls = {
  wf: "/resource/:id",
  public: "/public/:slug/resource/:id"
};

export const useResource = createQuery<
  ResourceResponse,
  ResourceRequest,
  Error
>({
  queryKey: ["resource"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    const { api: viewResourcesAPI, ...rest } = variables;
    // sleep 3000
    // await new Promise((resolve) => setTimeout(resolve, 200));
    return api<Omit<ResourceRequest, "api">, ResourceResponse>({
      method: "GET",
      url: urls[viewResourcesAPI],
      requestSchema: ResourceRequestVariables,
      responseSchema: GetResourceAgendaResourceResponseSchema,
      body: rest
    });
  }
});

export const useResourceSuspense = createSuspenseQuery<
  ResourceResponse,
  ResourceRequest,
  Error
>({
  queryKey: ["resource"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    const { api: viewResourcesAPI, ...rest } = variables;
    return api<Omit<ResourceRequest, "api">, ResourceResponse>({
      method: "GET",
      url: urls[viewResourcesAPI],
      requestSchema: ResourceRequestVariables,
      responseSchema: GetResourceAgendaResourceResponseSchema,
      body: rest
    });
  }
});
